import React, { useState } from 'react';

import {
  IBlockedReviewer,
  IPeerReviewListItem,
  IPeerReviewListItemWithRescission
} from '@publishing-engine/domain/views';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Text, Flex, Box, Tooltip } from '@chakra-ui/react';
import {
  Badge,
  Button,
  ButtonIcon,
  ButtonText,
  Pagination,
  Table,
  useToast
} from '@morressier/alembic';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import ReviewDetails from './ReviewDetails';
import {
  fetchArticleWorkflowBySubmissionId,
  fetchFormSchema,
  fetchEnqueuedReviewers,
  fetchBlockedReviewers,
  ensureUser
} from 'src/api/queries';
import ReviewsTable from './ReviewsTable';
import EmptyReviewsTable from './EmptyReviewsTable';
import {
  endStates,
  IEndState,
  IFirstAssessmentRescission,
  IReviewer,
  IInvitedReviewer,
  IWorkPhase,
  IPeerReviewWithdrawActions,
  ICoReview,
  IRoleType,
  IPeerReviewSummaryCounts
} from '@publishing-engine/domain/models';
import AuthorDetailsModal from '../AuthorsTab/AuthorDetailsModal';
import RescindReviewModal, {
  RescindReviewFormValues
} from 'src/components/Review/RescindReviewModal';
import {
  ICommandResponse,
  useInviteReviewer,
  useReinviteReviewer,
  useRequestReviewInRevision,
  useRescindFirstAssessment,
  useResendDeclinedReviewInvitation,
  useResendExpiredReviewInvitation,
  useResendWithdrawnReviewInvitation
} from 'src/api/commands';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import { useRateReview } from 'src/api/commands';
import { getReviewerNameOrEmail, RATING_OPTIONS } from 'src/utils/reviews';
import ReviewQueue from './ReviewQueue';
import { DeclineReviewAdditionalInformation } from 'src/components/Review/DeclinedReviewInformation';
import { GrantReviewExtensionModal } from './GrantReviewExtensionModal';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { EditorialRevisionBanner } from '../PhaseBanner/editorial/RevisionBanner';
import RevisionReviewsTable from './RevisionReviewsTable';
import { ReinviteReviewerModal } from './ReinviteReviewerModal';
import { RemoveReviewerModal } from './RemoveReviewerModal';
import { useKeycloak } from 'src/components/AuthHOC';
import { ReviewsHistory } from './ReviewRoundHistory/ReviewsHistory';
import { useSubmissionReviewsData } from '../../../contexts/SubmissionReviewsDataProvider';
import SubmittedAuthorDetailsModal from '../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';
import RsInviteModal from './ReviewerSearch';
import AuthorSubmissionFormDataProvider, {
  useSubmissionContextData
} from 'src/contexts/SubmissionContextProvider';
import {
  faEnvelope,
  faInfoCircle,
  faPeopleArrows,
  faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pluralizeString } from 'src/utils/string';
import { WithdrawReviewInvitationModal } from './WithdrawReviewInvitationModal';
import EmptyState from '../BoardMemberReviewsTab/EmptyState';
import AddReviewerToQueueModal from './ReviewQueue/AddReviewerToQueueModal';
import ComposeEmailModal, {
  AdHocModalOpen
} from '../AuthorsTab/ComposeEmailModal';
import RateReviewWithEmail from './RateReviewWithEmail';
import { ReviewersProvider } from '../../../contexts/ReviewersProvider';
import AddEmailsModal from './ReviewerSearch/AddEmailsModal';
import AddBoardMembersModal from './AddBoardMembersModal';
import ReassignBoardMemberModal from './ReassignBoardMemberModal';
import { ExistingReviewersProvider } from '../../../contexts/ExistingReviewersProvider';
import ProtectedActionButton from '../../ProtectedAction/Button';
import ReviewsHistoryTable from './ReviewsHistoryTable';

const getReviewStatusSummary = (
  reviewsCount: number,
  reviewSummaryCounts: IPeerReviewSummaryCounts | undefined
) => {
  const totalReviews = reviewSummaryCounts?.total ?? reviewsCount;

  const statusMessages = [
    { count: reviewSummaryCounts?.completed || 0, label: 'Reviews Completed' },
    { count: reviewSummaryCounts?.inProgress || 0, label: 'In Progress' },
    { count: reviewSummaryCounts?.invited || 0, label: 'Invited' },
    {
      count: reviewSummaryCounts?.declinedExpired || 0,
      label: 'Declined/Expired'
    },
    { count: reviewSummaryCounts?.withdrawn || 0, label: 'Withdrawn' }
  ];

  const convertedString = statusMessages
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => {
      if (label === 'Reviews Completed') {
        return `${count} ${pluralizeString('Review', count)} Completed`;
      }
      return `${count} ${label}`;
    })
    .join(', ');

  return `${totalReviews} Requested Reviews: ${convertedString}`;
};

const getRevisionReviewStatusSummary = (
  reviewsCount: number,
  reviews: IPeerReviewListItem[] = [],
  reviewSummaryCounts: IPeerReviewSummaryCounts | undefined
) => {
  const totalReviews = reviewSummaryCounts?.total ?? reviewsCount;

  const statusCount = {
    completed: 0,
    inProgress: 0,
    invited: 0,
    canBeReinvited: 0,
    declinedExpired: 0,
    withdrawn: 0
  };

  reviews.forEach(review => {
    switch (review.workflow.currentState) {
      case 'submittedRevisionAssessment':
        statusCount.completed++;
        break;
      case 'revisionAssessment':
      case 'rescinded':
        statusCount.inProgress++;
        break;
      case 'revisionInvited':
        statusCount.invited++;
        break;
      case 'awaitingRevisionReviewRequest':
        statusCount.canBeReinvited++;
        break;
      case 'declined':
      case 'expired':
        statusCount.declinedExpired++;
        break;
      case 'withdrawn':
        statusCount.withdrawn++;
        break;
      default:
        break;
    }
  });

  const statusMessages = [
    { count: reviewSummaryCounts?.completed || 0, label: 'Reviews Completed' },
    { count: reviewSummaryCounts?.inProgress || 0, label: 'In Progress' },
    { count: reviewSummaryCounts?.invited || 0, label: 'Invited' },
    {
      count: reviewSummaryCounts?.declinedExpired || 0,
      label: 'Declined/Expired'
    },
    { count: reviewSummaryCounts?.withdrawn || 0, label: 'Withdrawn' },
    { count: reviewSummaryCounts?.canBeReinvited || 0, label: 'CanBeInvited' }
  ];

  const convertedString = statusMessages
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => {
      if (label === 'Reviews Completed') {
        return `${count} ${pluralizeString('Review', count)} Completed`;
      } else if (label === 'CanBeInvited') {
        return `${count} ${pluralizeString(
          'Review',
          count
        )} from the last round can be restarted or withdrawn.`;
      }
      return `${count} ${label}`;
    })
    .join(', ');

  return `${totalReviews} Requested Reviews: ${convertedString}`;
};

type ReviewsTabProps = {
  roleContext: IRoleType;
};

/**
 * Everything the Rate Review modal needs, captured when the action is picked.
 * It is deliberately not just a review id: the action is also offered from the
 * round-history table, whose reviews can belong to a round that is not in the
 * paginated reviews list (or to a previous journal), so they cannot be looked
 * up there.
 */
type RateReviewTarget = {
  reviewId: string;
  reviewerId: string;
  email: string;
  receiveFeedback?: string;
};

const ReviewsTab: React.FC<ReviewsTabProps> = ({ roleContext }) => {
  const { availableActions, workflowResponse, isRevisionRound } =
    useSubmissionData();

  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState<IReviewer | null>(null);
  const [isInviteReviewerModalOpen, setIsInviteReviewerModalOpen] =
    useState(false);
  const [isAssignReviewerModalOpen, setIsAssignReviewerModalOpen] =
    useState(false);
  const [isAddBoardMembersModalOpen, setIsAddBoardMembersModalOpen] =
    useState(false);
  const [reassignBoardMember, setReassignBoardMember] = useState<{
    reviewId: string;
    reviewerId: string;
    reviewerName: string;
    workflowCurrentState?: string;
  } | null>(null);
  const [isAddReviewerToQueueModalOpen, setIsAddReviewerToQueueModalOpen] =
    useState(false);
  const [rateReviewTarget, setRateReviewTarget] =
    useState<RateReviewTarget | null>(null);
  const [showRescindReviewModal, setShowRescindReviewModal] = useState<
    string | null
  >(null);
  const [showDeclineReviewReason, setShowDeclineReviewReason] = useState<
    string | null
  >(null);
  const [showExtensionGrantedModal, setShowExtensionGrantedModal] = useState<
    string | null
  >(null);
  const [showReinviteReviewerModal, setShowReinviteReviewerModal] = useState<
    string | null
  >(null);
  const [historyReinvite, setHistoryReinvite] =
    useState<IInvitedReviewer | null>(null);
  const [showRemoveReviewerModal, setShowRemoveReviewer] = useState<
    string | null
  >(null);
  const [showWithdrawReviewModal, setShowWithdrawReviewModal] = useState<{
    actionName: IPeerReviewWithdrawActions;
    reviewerId: string;
    reviewRequestId: string;
  } | null>(null);
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });
  const { submissionContext } = useSubmissionContextData();
  const {
    submissionId,
    submissionContextId: ctxId,
    organizationId
  } = useSubmissionData();
  const { reviews, page, setPage, totalPages } = useSubmissionReviewsData();

  const toast = useToast();
  const queryClient = useQueryClient();
  const { currentUser } = useKeycloak();

  const reinvitedReview = reviews?.items?.find(
    review => review.id === showReinviteReviewerModal
  );

  // Get form schema for first assessment
  const { data: articleWorkflow } = useQuery({
    queryKey: ['article-workflow', ctxId, submissionId],
    queryFn: () =>
      ctxId ? fetchArticleWorkflowBySubmissionId(submissionId, ctxId) : null
  });

  // on revision phase, where revision is not submitted yet,
  // we still want to use firstAssessment formSchema to show firstAssessment Data.
  // That is why we added below conditional check
  const reviewSchemaId = workflowResponse.revisionNumber
    ? articleWorkflow?.article_workflow?.formSchemas?.reviewerRevisionAssessment
    : articleWorkflow?.article_workflow?.formSchemas?.reviewerFirstAssessment;
  const { data: schemaData } = useQuery({
    queryKey: ['submission-context', ctxId, 'schema', reviewSchemaId],
    queryFn: () =>
      ctxId && reviewSchemaId ? fetchFormSchema(ctxId, reviewSchemaId) : null
  });

  const reviewsQueryKey = [
    'submission-context',
    ctxId,
    submissionId,
    'reviews'
  ];

  const { data } = useQuery({
    queryKey: [...reviewsQueryKey, 'blocked-reviewers'],
    queryFn: () => (ctxId ? fetchBlockedReviewers(ctxId, submissionId) : null)
  });
  const blockedReviewers = data?.items || [];

  const { data: reviewerInvitations } = useQuery({
    queryKey: [
      'submission-context',
      ctxId,
      submissionId,
      'reviewer-invitations'
    ],
    queryFn: () =>
      ctxId ? fetchEnqueuedReviewers(ctxId, submissionId) : undefined
  });

  const selectedReviewLookupId =
    selectedReviewId || showRescindReviewModal || showExtensionGrantedModal;

  // Carried-over reviewers come back in `carryingToNextRound` rather than in the
  // paginated `items`, so "View Review" on that table only resolves if the
  // lookup covers both lists. `items` is searched first, so a listed review is
  // still the one that wins when an id appears in both.
  const selectedReview =
    reviews?.items?.find(review => review.id === selectedReviewLookupId) ??
    reviews?.carryingToNextRound?.find(
      review => review.id === selectedReviewLookupId
    );

  // Entry points in the review tables hand over a review id; resolve it here so
  // the modal is driven by data rather than by a lookup on every render.
  const openRateReview = (reviewId: string) => {
    const review = reviews?.items?.find(r => r.id === reviewId);
    if (!review?.canRateReview) return;

    setRateReviewTarget({
      reviewId: review.id,
      reviewerId: review.reviewer.id,
      email: review.reviewer.email,
      receiveFeedback: (review as IPeerReviewListItemWithRescission).caseData
        .firstAssessment?.receiveFeedback
    });
  };

  const reviewsQuery = ['submission-context', ctxId, submissionId, 'reviews'];
  const { mutateAsync: mutateRateReview } = useRateReview({
    submissionId,
    organizationId,
    reviewId: rateReviewTarget?.reviewId ?? '',
    submissionContextId: ctxId,
    reviewerId: rateReviewTarget?.reviewerId ?? '',
    queryKey: reviewsQuery // fetch reviews query key
  });

  const { mutateAsync: mutateRescindReview } = useRescindFirstAssessment({
    submissionId,
    reviewId: selectedReview?.id ?? '',
    organizationId,
    submissionContextId: ctxId,
    reviewerId: selectedReview?.reviewer.id ?? '',
    queryKey: reviewsQuery // fetch reviews query key
  });

  const currentState = workflowResponse.workflowData.currentState as IWorkPhase;

  const { mutateAsync: mutateAwaitingRevisionReviewRequest } =
    useReinviteReviewer({
      queryKey: reviewsQueryKey,
      submissionContextId: ctxId,
      submissionId,
      organizationId
    });

  const { mutateAsync: mutateDeclineReviewInvitation } =
    useResendDeclinedReviewInvitation({
      phase: currentState,
      submissionId,
      submissionContextId: ctxId,
      queryKey: reviewsQuery
    });

  const { mutateAsync: mutateExpiredReviewInvitation } =
    useResendExpiredReviewInvitation({
      phase: currentState,
      submissionId,
      submissionContextId: ctxId,
      queryKey: reviewsQuery,
      organizationId
    });

  const { mutateAsync: mutateWithdrawnReviewInvitation } =
    useResendWithdrawnReviewInvitation({
      phase: currentState,
      submissionId,
      submissionContextId: ctxId,
      queryKey: reviewsQuery,
      organizationId
    });

  const reviewListKey = ['review-list', { ctxId, submissionId }];

  const inviteReviewerMutation = useInviteReviewer(
    submissionId,
    ctxId,
    reviewListKey,
    organizationId,
    'automaticBoardMember'
  );

  const requestReviewInRevisionMutation = useRequestReviewInRevision(
    submissionId,
    ctxId,
    reviewListKey,
    organizationId,
    'automaticBoardMember'
  );

  /**
   * Picks the command that re-invites a reviewer, from the review's workflow
   * state. The state arrives as a plain string — the modals read it off a
   * review and hand it straight over — so an unrecognised one returns
   * `undefined` rather than falling out of the switch, and the caller reports
   * it instead of dressing it up as a rejected command.
   */
  const resendInviteMutations = (
    currentState: string | undefined,
    reviewerId: string,
    reviewRequestId: string,
    emails?: IEmailCommandPayload
  ): undefined | Promise<ICommandResponse> => {
    const submissionState = workflowResponse.workflowData
      .currentState as IWorkPhase;
    // A review closed for revision is re-invited through the revision-review
    // request, the same mapping the round-history modal makes for `closed`.
    const reinviteState =
      currentState === 'closed'
        ? 'awaitingRevisionReviewRequest'
        : currentState;

    switch (reinviteState) {
      case 'expired':
        // if current review state is expired and submission state is revisionAssessment
        // we want to use `RequestRevisionReview` action to re-invite reviewer
        if (
          [
            'revisionAssessment',
            'revisionPeerReviewInvitation',
            'revisionPeerReviewPending',
            'revisionPeerReviewFinished'
          ].includes(submissionState)
        ) {
          return mutateAwaitingRevisionReviewRequest({
            reviewId: reviewRequestId,
            reviewerId,
            emails
          });
        }

        return mutateExpiredReviewInvitation({
          reviewerId: reviewerId,
          reviewRequestId,
          emails
        });

      case 'declined':
        return mutateDeclineReviewInvitation({
          reviewerId: reviewerId,
          reviewRequestId,
          organizationId,
          emails
        });

      case 'withdrawn': {
        const review = reviews?.items?.find(r => r.id === reviewRequestId);
        const previousState =
          review?.workflow.previousState ??
          historyReinvite?.workflowPreviousState;
        const subtype =
          previousState === 'invited' || previousState === 'revisionInvited'
            ? 'invited'
            : 'assessment';
        return mutateWithdrawnReviewInvitation({
          reviewerId: reviewerId,
          reviewRequestId,
          emails,
          subtype
        });
      }
      case 'awaitingRevisionReviewRequest':
        return mutateAwaitingRevisionReviewRequest({
          reviewId: reviewRequestId,
          reviewerId,
          emails
        });

      default:
        // Only the states above can be re-invited from, so there is no command
        // to send. `undefined` is the caller's cue to say so.
        return undefined;
    }
  };

  const formFieldData = schemaData?.documentSchema!;

  const reviewFormFields = new ReviewFormFields(formFieldData);

  const config = {
    reviewScore: reviewFormFields.getFieldConfig('ReviewScore'),
    //#region  submission score fields
    originality: reviewFormFields.getFieldConfig('Originality'),
    significance: reviewFormFields.getFieldConfig('Significance'),
    rigour: reviewFormFields.getFieldConfig('Rigour'),
    clarity: reviewFormFields.getFieldConfig('Clarity'),
    impact: reviewFormFields.getFieldConfig('Impact'),
    //#endregion
    missingReference: reviewFormFields.getFieldConfig('MissingReference'),
    articleWellWritten: reviewFormFields.getFieldConfig('ArticleWellWritten'),
    areaOfExpertise: reviewFormFields.getFieldConfig('AreaOfExpertise'),
    conflictOfInterest: reviewFormFields.getFieldConfig('ConflictOfInterest'),
    canArticleBeShortened: reviewFormFields.getFieldConfig(
      'CanArticleBeShortened'
    ),
    meritFurtherCoverageUponPublication: reviewFormFields.getFieldConfig(
      'MeritFurtherCoverageUponPublication'
    ),
    betterSuitedForAnotherJournal: reviewFormFields.getFieldConfig(
      'BetterSuitedForAnotherJournal'
    ),
    webOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    peerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    reviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    consentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite'),
    receiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    unaddressedReviewComments: reviewFormFields.getFieldConfig(
      'UnaddressedReviewComments'
    )
  };

  if (selectedReviewId && selectedReview) {
    return (
      <ReviewDetails
        review={selectedReview}
        onBackToReviews={() => setSelectedReviewId(null)}
        reviewFieldConfig={config}
      />
    );
  }

  if (selectedAuthor) {
    return selectedAuthor?.id ? (
      <SubmittedAuthorDetailsModal
        userId={selectedAuthor?.id || ''}
        author={{
          ...selectedAuthor,
          affiliation:
            selectedAuthor.affiliationDetails?.organizationName || '',
          ringgoldId: selectedAuthor.affiliationDetails?.ringgoldId || ''
        }}
        onBackClick={() => setSelectedAuthor(null)}
        backButtonText="Back to Reviews"
        showReviewerActivity={true}
        roleContext={roleContext}
        reviewId={reviews?.items[0]?.id}
      />
    ) : (
      <AuthorDetailsModal
        author={{
          ...selectedAuthor,
          affiliation:
            selectedAuthor.affiliationDetails?.organizationName || '',
          ringgoldId: selectedAuthor.affiliationDetails?.ringgoldId || ''
        }}
        onBackClick={() => setSelectedAuthor(null)}
        backButtonText="Back to Reviews"
      />
    );
  }

  const reviewsCount = reviews?.total || 0;

  const isAssignReviewersEnabled = submissionContext?.config.assignReviewers;
  const isBoardMemberReviewerAssignmentEnabled =
    submissionContext?.config.boardMemberReviewerAssigment;

  const invitedReviewers = reviews?.items.map(review => review.reviewer) || [];
  const coReviewer = reviews?.items
    .map(review => (review.coReview ? review.coReview.coReviewer : undefined))
    .filter(Boolean) as ICoReview['coReviewer'][];
  const enqueuedReviewers =
    reviewerInvitations?.queue?.map(invitation => ({
      email: invitation.email
    })) || [];

  // reviewers who have been invited, are in the queue, or have declined the review request
  // should not be invited again
  const existingReviewers:
    | IReviewer[]
    | Pick<IReviewer, 'email'>[]
    | IBlockedReviewer[] = [
    ...invitedReviewers,
    ...enqueuedReviewers,
    ...blockedReviewers,
    ...coReviewer
  ];

  const openReinviteModal = (reviewRequestId: string) => {
    setShowReinviteReviewerModal(reviewRequestId);
  };

  /**
   * Resends the invitation and reports whether the command went through. The
   * modal owns the success message, so a rejected command — re-inviting someone
   * who is already invited, say — must not resolve as though it succeeded, or
   * the editor sees "Reviewer Reinvited" beside the error.
   */
  const resendReviewInvitation = async (
    reviewRequestId: string,
    reviewer: Pick<IReviewer, 'email' | 'id'>,
    currentState: string | undefined,
    emails?: IEmailCommandPayload
  ): Promise<boolean> => {
    const response = await resendInviteMutations(
      currentState,
      reviewer.id,
      reviewRequestId,
      emails
    );

    // No command matched the reviewer's state, so nothing was sent. Say that
    // rather than reporting a failure the server never returned.
    if (!response) {
      toast({
        variant: 'error',
        title: 'Cannot Resend Invitation',
        description:
          'This reviewer is not in a state their invitation can be resent from.'
      });
      return false;
    }

    if (!response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error while Resending invite'
      });
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ['revision-history'] });
    return true;
  };

  const handleWithdrawReviewer = (
    reviewRequestId: string,
    reviewer: IReviewer,
    actionName: IPeerReviewWithdrawActions
  ) => {
    setShowWithdrawReviewModal({
      reviewerId: reviewer.id,
      reviewRequestId,
      actionName
    });
  };

  const renderReviewTable = () => {
    const submissionCurrentState = workflowResponse.workflowData
      .currentState as ISubmissionWFState;
    const isUnderAuthorRevision = submissionCurrentState === 'revision';
    const isAwaitingReviews = [
      'immediateRevision',
      'assignment',
      'initialAssessment',
      'unsubmitted',
      'transferPending'
    ].includes(submissionCurrentState);

    if (isAwaitingReviews) {
      return renderAwaitingReviews();
    }

    if (
      endStates.includes(submissionCurrentState as IEndState) ||
      submissionCurrentState === 'preProduction' ||
      submissionCurrentState === 'revisionAssessment'
    ) {
      return renderReadOnlyReviewTable();
    }

    if (isUnderAuthorRevision) {
      return renderLockedReviewTable();
    }

    return isRevisionRound
      ? renderRevisionReviews()
      : renderFirstRoundReviews();
  };

  const renderLockedReviewTable = () => {
    const carriedOverReviews =
      reviews?.carryingToNextRound ??
      [];

    return (
      <>
        <EditorialRevisionBanner
          workflowData={workflowResponse.workflowData}
          submissionId={workflowResponse.submissionId}
          copyVariant={'reviews'}
        />

        {carriedOverReviews.length > 0 && (
          <>
            <Flex gap={2} flexDirection={'column'}>
              <Text textStyle="headingGroup">
                Carrying to Next Round
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label="Reviewers with completed reviews in previous rounds are carried over
                for your convenience, but you have full control over which
                reviewers are invited for the upcoming round."
                  borderRadius={4}
                >
                  <ButtonIcon
                    size="small"
                    ml={1}
                    ariaLabel="Help Icon"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  />
                </Tooltip>
              </Text>
            </Flex>

            <Flex gap={2} flexDirection={'column'}>
              <Text textStyle="headingItem">Reviewers with Completed Work</Text>
              <Table>
                <Table.Body>
                  {carriedOverReviews.map(review => {
                    return (
                      <Table.Row
                        key={review.id}
                        onClick={() => setSelectedReviewId(review.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.RowCell
                          flex={1}
                          textAlign="left"
                          onClick={() => setSelectedReviewId(review.id)}
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                        >
                          <Flex
                            flexDirection="column"
                            alignItems="flex-start"
                            gap={2}
                          >
                            <Box>
                              <Text textStyle="headingItem">
                                {getReviewerNameOrEmail(review.reviewer)}
                              </Text>
                              <Text textStyle="textContext">
                                {
                                  review.reviewer.affiliationDetails
                                    ?.organizationName
                                }
                              </Text>
                            </Box>
                            {review.coReview?.status === 'accepted' && (
                              <>
                                <Box>
                                  <Text textStyle="headingItem">
                                    {getReviewerNameOrEmail(
                                      review.coReview.coReviewer
                                    )}
                                  </Text>
                                  {review.coReview.coReviewer.affiliation && (
                                    <Text textStyle="textContext">
                                      {review.coReview.coReviewer.affiliation}
                                    </Text>
                                  )}
                                </Box>
                                <Badge variant="attention" size="context">
                                  <FontAwesomeIcon icon={faPeopleArrows} />
                                  Co-Reviewer
                                </Badge>
                              </>
                            )}
                          </Flex>
                        </Table.RowCell>
                        <Table.RowCell key="actions" textAlign="center">
                          <ButtonText
                            alignSelf="center"
                            onClick={() => setSelectedReviewId(review.id)}
                          >
                            View Review
                          </ButtonText>
                        </Table.RowCell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Flex>
          </>
        )}

        {reviewsCount > 0 && (
          <>
            <Text textStyle="headingGroup">
              Current Round History{' '}
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label="Reviewers invited for the round in progress and their review status"
                borderRadius={4}
              >
                <ButtonIcon
                  size="small"
                  ml={1}
                  ariaLabel="Help Icon"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                />
              </Tooltip>
            </Text>
            <ReviewsHistoryTable
              reviews={reviews?.items ?? []}
              onSelectedAuthor={setSelectedAuthor}
              onSelectReviewId={setSelectedReviewId}
            />
            {totalPages > 1 && (
              <Flex justifyContent="center">
                <Pagination
                  key={`readonly-reviews-pagination-${totalPages}-${page}`}
                  ariaLabel="Reviewers Pagination"
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </Flex>
            )}{' '}
          </>
        )}

        <ReviewQueue
          reviewerInvitations={reviewerInvitations}
          onViewReviewerDetails={setSelectedAuthor}
          onInviteReviewer={() => setIsAddReviewerToQueueModalOpen(true)}
        />
      </>
    );
  };

  const renderFirstRoundReviews = () => {
    if (reviews?.invitedReviewers.length) {
      return (
        <>
          <Text textStyle={'textContext'}>
            {getReviewStatusSummary(reviewsCount, reviews.reviewSummaryCounts)}
          </Text>

          <Flex gap={4}>
            <ProtectedWorkflowAction actionName="RequestReview">
              <Button
                onClick={() => setIsInviteReviewerModalOpen(true)}
                isDisabled={!availableActions.contains('RequestReview')}
                leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
              >
                Invite Reviewers
              </Button>
            </ProtectedWorkflowAction>
            {isAssignReviewersEnabled && (
              <ProtectedActionButton
                relation="decision_maker"
                entityType="journal_phase"
                entityId={`${ctxId}_${currentState}`}
                variant="secondary"
                onClick={() => setIsAssignReviewerModalOpen(true)}
                isDisabled={!availableActions.contains('RequestReview')}
              >
                Assign Reviewers
              </ProtectedActionButton>
            )}
            {isBoardMemberReviewerAssignmentEnabled && (
              <ProtectedWorkflowAction actionName="RequestReview">
                <Button
                  variant="primary"
                  onClick={() => setIsAddBoardMembersModalOpen(true)}
                  isDisabled={!availableActions.contains('RequestReview')}
                  iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
                >
                  Assign Reviewers
                </Button>
              </ProtectedWorkflowAction>
            )}
            {/* Filters Here */}
          </Flex>
          <ReviewsTable
            reviews={reviews?.items ?? []}
            onSelectReviewId={setSelectedReviewId}
            onSelectedAuthor={setSelectedAuthor}
            onRateReview={openRateReview}
            onRescindReview={setShowRescindReviewModal}
            onDeclineReviewReason={setShowDeclineReviewReason}
            onGrantExtension={setShowExtensionGrantedModal}
            onResendInvite={openReinviteModal}
            onWithdrawReviewer={handleWithdrawReviewer}
            onSendEmail={setOpenSendEmailModal}
            onReassignBoardMember={reviewId => {
              const review = reviews?.items.find(r => r.id === reviewId);
              if (review) {
                setReassignBoardMember({
                  reviewId,
                  reviewerId: review.reviewer.id,
                  reviewerName: getReviewerNameOrEmail(review.reviewer) || ''
                });
              }
            }}
          />
          {totalPages > 1 && (
            <Flex justifyContent="center">
              <Pagination
                key={`reviews-pagination-${totalPages}-${page}`}
                ariaLabel="Reviewers Pagination"
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </Flex>
          )}

          <ReviewQueue
            reviewerInvitations={reviewerInvitations}
            onViewReviewerDetails={setSelectedAuthor}
            onInviteReviewer={() => setIsAddReviewerToQueueModalOpen(true)}
          />
        </>
      );
    }

    return (
      <>
        <EmptyReviewsTable
          width="100%"
          onInviteReviewer={() => setIsInviteReviewerModalOpen(true)}
          onAssignReviewer={() => setIsAssignReviewerModalOpen(true)}
          onAddBoardMembers={() => setIsAddBoardMembersModalOpen(true)}
          entityId={`${ctxId}_${currentState}`}
        />
        <ReviewQueue
          reviewerInvitations={reviewerInvitations}
          onViewReviewerDetails={setSelectedAuthor}
          onInviteReviewer={() => setIsAddReviewerToQueueModalOpen(true)}
        />
      </>
    );
  };

  const renderRevisionReviews = () => {
    return (
      <>
        <Text textStyle={'textContext'}>
          {getRevisionReviewStatusSummary(
            reviewsCount,
            reviews?.items,
            reviews?.reviewSummaryCounts
          )}
        </Text>

        <Flex gap={4}>
          <ProtectedWorkflowAction actionName="RequestReviewInRevision">
            <Button
              onClick={() => setIsInviteReviewerModalOpen(true)}
              isDisabled={!availableActions.contains('RequestReviewInRevision')}
              leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
            >
              Invite Reviewers
            </Button>
          </ProtectedWorkflowAction>
          {isAssignReviewersEnabled && (
            <ProtectedActionButton
              relation="decision_maker"
              entityType="journal_phase"
              entityId={`${ctxId}_${currentState}`}
              variant="secondary"
              as="button"
              onClick={() => setIsAssignReviewerModalOpen(true)}
              isDisabled={!availableActions.contains('RequestReviewInRevision')}
            >
              Assign Reviewers
            </ProtectedActionButton>
          )}
          {isBoardMemberReviewerAssignmentEnabled && (
            <ProtectedWorkflowAction actionName="RequestReviewInRevision">
              <Button
                variant="primary"
                onClick={() => setIsAddBoardMembersModalOpen(true)}
                isDisabled={
                  !availableActions.contains('RequestReviewInRevision')
                }
                iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
              >
                Assign Reviewers
              </Button>
            </ProtectedWorkflowAction>
          )}
          {/* Filters Here */}
        </Flex>
        {reviews?.invitedReviewers.length ? (
          <>
            <RevisionReviewsTable
              reviews={reviews?.items ?? []}
              onRateReview={openRateReview}
              onSelectReviewId={setSelectedReviewId}
              onSelectedAuthor={setSelectedAuthor}
              onGrantExtension={setShowExtensionGrantedModal}
              onDeclineReviewReason={setShowDeclineReviewReason}
              onReinviteReviewer={setShowReinviteReviewerModal}
              onResendInvite={openReinviteModal}
              onWithdrawReviewer={handleWithdrawReviewer}
              onSendEmail={setOpenSendEmailModal}
              onRescindReview={setShowRescindReviewModal}
              onReassignBoardMember={reviewId => {
                const review = reviews?.items.find(r => r.id === reviewId);
                if (review) {
                  setReassignBoardMember({
                    reviewId,
                    reviewerId: review.reviewer.id,
                    reviewerName: getReviewerNameOrEmail(review.reviewer) || ''
                  });
                }
              }}
            />
            {totalPages > 1 && (
              <Flex justifyContent="center">
                <Pagination
                  key={`revision-reviews-pagination-${totalPages}-${page}`}
                  ariaLabel="Reviewers Pagination"
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </Flex>
            )}
          </>
        ) : (
          <EmptyReviewsTable
            onInviteReviewer={() => setIsInviteReviewerModalOpen(true)}
            onAssignReviewer={() => setIsAssignReviewerModalOpen(true)}
            width="100%"
            renderInviteCallToAction={false}
            entityId={`${ctxId}_${currentState}`}
          />
        )}

        <ReviewQueue
          reviewerInvitations={reviewerInvitations}
          onViewReviewerDetails={setSelectedAuthor}
          onInviteReviewer={() => setIsAddReviewerToQueueModalOpen(true)}
        />
      </>
    );
  };

  const renderReadOnlyReviewTable = () => {
    return reviewsCount > 0 ? (
      <>
        <ReviewsHistoryTable
          reviews={reviews?.items ?? []}
          onSelectReviewId={setSelectedReviewId}
          onSelectedAuthor={setSelectedAuthor}
        />
        {totalPages > 1 && (
          <Flex justifyContent="center">
            <Pagination
              key={`readonly-reviews-pagination-${totalPages}-${page}`}
              ariaLabel="Reviewers Pagination"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Flex>
        )}

        {reviewerInvitations?.queue?.length ? (
          <ReviewQueue
            reviewerInvitations={reviewerInvitations}
            onViewReviewerDetails={setSelectedAuthor}
            renderInviteCallToAction={false}
          />
        ) : null}
      </>
    ) : (
      <EmptyReviewsTable
        onInviteReviewer={() => setIsInviteReviewerModalOpen(true)}
        onAssignReviewer={() => setIsAssignReviewerModalOpen(true)}
        width="100%"
        renderInviteCallToAction={false}
        title="No Completed Reviews"
        description="To effectively capture feedback in the future, ensure that all reviews are fully finalized and formally approved before moving to the next phase of the workflow. "
        entityId={`${ctxId}_${currentState}`}
      />
    );
  };

  const AwaitingReviewSubTitle = () => (
    <Flex flexDirection="column" gap={2}>
      <Text>
        Once the submission is assigned to an editor, the editor can review the
        submission and any available assessments or reviewer feedback, and then
        proceed with the appropriate editorial decision.
      </Text>
    </Flex>
  );

  const renderAwaitingReviews = () => (
    <Flex flexDirection={'column'} gap={5}>
      <EmptyState
        title="Awaiting Peer Review Decision"
        subTitle={<AwaitingReviewSubTitle />}
        headingText=""
        marginTops={1}
        paddings={1}
      />
    </Flex>
  );

  const handleRateReview = async (
    rating: string,
    justification?: string,
    emails?: IEmailCommandPayload
  ) => {
    const response = await mutateRateReview({
      reviewScore: { score: rating, justification },
      reviewRequestId: rateReviewTarget?.reviewId,
      emails
    });
    if (response.isSuccess) {
      const ratingOption = RATING_OPTIONS[rating];
      toast({
        variant: 'success',
        title: `Review Rated as ${ratingOption?.value} (${ratingOption?.label})`,
        description:
          rateReviewTarget?.receiveFeedback === 'yes'
            ? 'The Reviewer will receive the results once a decision for this Submission is made.'
            : ''
      });
      setRateReviewTarget(null);
    } else {
      toast({
        variant: 'error',
        title: 'Error Rating Review'
      });
    }
  };

  const handleRescindReview = async (formValues: RescindReviewFormValues) => {
    const rescindReview: IFirstAssessmentRescission = {
      feedback: formValues.feedback,
      attachments: formValues.attachments,

      actionBy: {
        userId: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName
      }
    };

    const response = await mutateRescindReview({
      rescindReview,
      reviewRequestId: selectedReview?.id
    });
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Review Rescinded',
        description: `${selectedReview?.reviewer.firstName} ${selectedReview?.reviewer.lastName} will receive your feedback by email, which will be recorded in their review.`,
        isClosable: true
      });

      setShowRescindReviewModal(null);
    }
  };

  const handleAutoAssignReviewers = async (emails: string[]) => {
    let usersResponse: { message: string; email: string; userId: string }[];

    try {
      usersResponse = await Promise.all(emails.map(email => ensureUser(email)));
    } catch (e: any) {
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description:
          e?.response?.data?.message ||
          'We are unable to invite these reviewers at this time.',
        isClosable: true
      });

      return 0;
    }

    const results: ICommandResponse[] = [];

    const mutation = availableActions.contains('RequestReviewInRevision')
      ? requestReviewInRevisionMutation
      : inviteReviewerMutation;

    for (const { userId } of usersResponse) {
      results.push(await mutation.mutateAsync({ reviewer: { id: userId } }));
    }

    const successCount = results.filter(r => r.isSuccess).length;
    const failedCount = results.length - successCount;
    if (failedCount) {
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description: `${failedCount} Reviewers Failed to Invite, Please try again.`,
        isClosable: true
      });
    }

    if (successCount > 0) {
      toast({
        variant: 'success',
        title: 'Invitation Sent',
        description: `Invitation email sent to (${successCount}) reviewers.`,
        isClosable: true
      });
    }
  };

  const handleAddBoardMembers = async (userIds: string[]) => {
    const mutation = availableActions.contains('RequestReviewInRevision')
      ? requestReviewInRevisionMutation
      : inviteReviewerMutation;

    const results: ICommandResponse[] = [];
    for (const id of userIds) {
      results.push(await mutation.mutateAsync({ reviewer: { id } }));
    }

    const successCount = results.filter(r => r.isSuccess).length;
    const failedCount = results.length - successCount;
    if (failedCount) {
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description: `${failedCount} ${pluralizeString(
          'board member',
          failedCount
        )} failed to be assigned. Please try again.`,
        isClosable: true
      });
    }

    if (successCount > 0) {
      toast({
        variant: 'success',
        title: 'Invitation Sent',
        description: `Assigned ${successCount} ${pluralizeString(
          'board member',
          successCount
        )}.`,
        isClosable: true
      });
    }
  };

  const handleReassignBoardMember = async () => {
    if (!reassignBoardMember) return;

    const mutation = availableActions.contains('RequestReviewInRevision')
      ? requestReviewInRevisionMutation
      : inviteReviewerMutation;

    const res = await mutation.mutateAsync({
      reviewer: { id: reassignBoardMember.reviewerId },
      reviewRequestId: reassignBoardMember.reviewId
    });

    if (res.isSuccess) {
      await queryClient.invalidateQueries({ queryKey: ['revision-history'] });
      await queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
      toast({
        variant: 'success',
        title: 'Reviewer Reassigned',
        description: 'The board member has been reassigned to this submission.',
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Reassignment Failed',
        description: 'Failed to reassign the reviewer. Please try again.',
        isClosable: true
      });
    }
  };

  return (
    <Flex flexDirection="column" gap={8}>
      <Flex flexDirection="column">
        <Text textStyle="headingSection">Peer Reviews</Text>
        <Flex flexDirection="column" gap={6}>
          {renderReviewTable()}
        </Flex>
      </Flex>

      <ReviewsHistory
        onReinviteReviewer={setHistoryReinvite}
        onReassignBoardMember={reviewer =>
          setReassignBoardMember({
            reviewId: reviewer.reviewId,
            reviewerId: reviewer.reviewerId,
            reviewerName: getReviewerNameOrEmail(reviewer) || '',
            workflowCurrentState: reviewer.workflowCurrentState
          })
        }
        onRateReview={(reviewer, receiveFeedback) => {
          if (!reviewer.canRateReview) return;

          setRateReviewTarget({
            reviewId: reviewer.reviewId,
            reviewerId: reviewer.reviewerId,
            email: reviewer.email,
            receiveFeedback
          });
        }}
      />

      {rateReviewTarget && (
        <RateReviewWithEmail
          email={rateReviewTarget.email}
          reviewId={rateReviewTarget.reviewId}
          reviewRequestId={rateReviewTarget.reviewerId}
          onClose={() => setRateReviewTarget(null)}
          onRateReview={handleRateReview}
          receiveFeedback={rateReviewTarget.receiveFeedback}
          organizationId={organizationId}
        />
      )}

      {openSendEmailModal.status && (
        <ComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          email={openSendEmailModal.defaultEmail}
        />
      )}

      {showExtensionGrantedModal && (
        <GrantReviewExtensionModal
          isOpen
          onClose={() => setShowExtensionGrantedModal(null)}
          review={selectedReview}
          organizationId={organizationId}
        />
      )}

      {showRescindReviewModal && (
        <RescindReviewModal
          isOpen
          submissionId={submissionId}
          onClose={() => setShowRescindReviewModal(null)}
          reviewer={
            (selectedReview as IPeerReviewListItemWithRescission).reviewer
          }
          submissionContextId={ctxId}
          onRescindReview={handleRescindReview}
        />
      )}

      {showDeclineReviewReason && (
        <DeclineReviewAdditionalInformation
          onClose={() => setShowDeclineReviewReason(null)}
          review={
            reviews?.items?.find(
              review => review.id === showDeclineReviewReason
            )!
          }
        />
      )}

      {isInviteReviewerModalOpen && (
        <AuthorSubmissionFormDataProvider>
          <ReviewersProvider>
            <RsInviteModal
              closeModal={() => setIsInviteReviewerModalOpen(false)}
              submissionId={submissionId}
              blockedReviewers={blockedReviewers}
            />
          </ReviewersProvider>
        </AuthorSubmissionFormDataProvider>
      )}

      {isAssignReviewerModalOpen && (
        <ExistingReviewersProvider existingReviewers={[...existingReviewers]}>
          <AddEmailsModal
            onClose={() => setIsAssignReviewerModalOpen(false)}
            onAddEmails={(emails: string[]) =>
              handleAutoAssignReviewers(emails)
            }
            buttonLabel="Assign"
            blockedReviewers={blockedReviewers}
            requireUserExistence={true}
          />
        </ExistingReviewersProvider>
      )}

      {isAddBoardMembersModalOpen && (
        <AddBoardMembersModal
          isOpen
          onClose={() => setIsAddBoardMembersModalOpen(false)}
          organizationId={organizationId}
          submissionContextId={ctxId}
          submissionId={submissionId}
          existingReviewers={existingReviewers}
          blockedReviewers={blockedReviewers}
          onAddBoardMembers={handleAddBoardMembers}
        />
      )}

      {reassignBoardMember && (
        <ReassignBoardMemberModal
          isOpen
          onClose={() => setReassignBoardMember(null)}
          reviewerName={reassignBoardMember.reviewerName}
          onReassign={handleReassignBoardMember}
        />
      )}

      {reinvitedReview && (
        <ReinviteReviewerModal
          onClose={() => setShowReinviteReviewerModal(null)}
          reviewId={reinvitedReview.id}
          reviewerId={reinvitedReview.reviewer.id}
          reviewerEmail={reinvitedReview.reviewer.email}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={ctxId}
          reviewsQueryKey={reviewsQueryKey}
          isCoReviewed={reinvitedReview.coReview?.status === 'accepted'}
          // The review's own state, unchecked: the modal is only reachable for
          // states a command exists for, and `resendInviteMutations` reports the
          // ones it cannot map instead of pretending they failed.
          status={reinvitedReview.workflow.currentState}
          submissionState={workflowResponse.workflowData.currentState}
          onReinvite={resendReviewInvitation}
          previousState={reinvitedReview.workflow.previousState}
        />
      )}

      {historyReinvite && (
        <ReinviteReviewerModal
          onClose={() => setHistoryReinvite(null)}
          reviewId={historyReinvite.reviewId}
          reviewerId={historyReinvite.reviewerId}
          reviewerEmail={historyReinvite.email}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={ctxId}
          reviewsQueryKey={reviewsQueryKey}
          isCoReviewed={false}
          status={
            historyReinvite.workflowCurrentState === 'closed'
              ? 'awaitingRevisionReviewRequest'
              : [
                  historyReinvite.workflowCurrentState,
                  historyReinvite.workflowPreviousState,
                  historyReinvite.status
                ].find(state =>
                  [
                    'declined',
                    'expired',
                    'withdrawn',
                    'awaitingRevisionReviewRequest'
                  ].includes(state ?? '')
                ) ?? historyReinvite.status
          }
          submissionState={workflowResponse.workflowData.currentState}
          onReinvite={resendReviewInvitation}
          previousState={historyReinvite.workflowPreviousState}
        />
      )}

      {showRemoveReviewerModal && (
        <RemoveReviewerModal
          onClose={() => setShowRemoveReviewer(null)}
          reviewId={showRemoveReviewerModal}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={ctxId}
          reviewsQueryKey={reviewsQueryKey}
        />
      )}

      {showWithdrawReviewModal && (
        <WithdrawReviewInvitationModal
          protectedWFActionName={showWithdrawReviewModal.actionName}
          onClose={() => setShowWithdrawReviewModal(null)}
          reviewRequestId={showWithdrawReviewModal.reviewRequestId}
          reviewerId={showWithdrawReviewModal.reviewerId}
          submissionId={submissionId}
          submissionContextId={ctxId}
          phase={currentState}
          organizationId={organizationId}
        />
      )}

      {isAddReviewerToQueueModalOpen && (
        <AuthorSubmissionFormDataProvider>
          <ReviewersProvider>
            <AddReviewerToQueueModal
              closeModal={() => setIsAddReviewerToQueueModalOpen(false)}
              submissionId={submissionId}
              blockedReviewers={blockedReviewers}
            />
          </ReviewersProvider>
        </AuthorSubmissionFormDataProvider>
      )}
    </Flex>
  );
};

export default ReviewsTab;
